import moment from "moment";
import { getFormattedDateString, Toast } from "utility/helpers";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import JSPDF from "jspdf";
import "jspdf-autotable";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  AlignmentType,
  WidthType,
  TableLayoutType,
  VerticalAlign,
  PageNumber,
  Header,
  Footer,
  HeightRule,
} from "docx";
import { saveAs } from "file-saver";
import { modifiedStatuses, REPORT_COLOR_SCHEMES } from "./constants";

const getRemarks = (data, type, recommendedBudget) => {
  let remarks = `Allocation # ${data?.id}`;

  if (type === "request") {
    remarks = recommendedBudget
      ? Number(recommendedBudget)
      : "No funds available";
  }

  return remarks;
};

const getProjectRecipients = (projectId, data, type, recommendedBudget) => {
  const formattedRecipients = [];
  const reportType = type === "request" ? "Request" : "Endorsement";

  if (data?.length) {
    data.forEach((recipient) => {
      const congressman = recipient?.congressman?.fullName;

      const idx = formattedRecipients.findIndex(
        (el) => el.projectId === projectId && el.cong === congressman
      );

      if (idx === -1) {
        const newData = {
          projectId,
          cong: congressman,
          amount: recipient?.budget,
          remarks: getRemarks(recipient, type, recommendedBudget),
        };
        formattedRecipients.push(newData);
      } else {
        formattedRecipients[idx] = {
          ...formattedRecipients[idx],
          amount: formattedRecipients[idx].amount + recipient.budget,
        };
      }
    });
  }

  const totalAmount = formattedRecipients.reduce((sum, recipient) => {
    return sum + (recipient.amount || 0);
  }, 0);

  const totalRecommendation = formattedRecipients.reduce((sum, recipient) => {
    return typeof recipient.remarks === "number"
      ? sum + (recipient.remarks || 0)
      : "";
  }, 0);

  const totalRemark =
    typeof totalRecommendation === "number"
      ? totalRecommendation.toLocaleString("en-US")
      : "";

  formattedRecipients.push({
    cong: `TOTAL ${reportType.toUpperCase()}`,
    amount: totalAmount,
    remarks: type === "request" ? totalRemark : "",
  });

  return formattedRecipients;
};

const getTableIndex = (idx, table) => {
  let index = idx + 1;

  if (index === table.length) index = "";

  return index;
};

const formatRemarks = (remarks, type) => {
  let newRemark = remarks;

  if (type === "request") {
    newRemark =
      typeof remarks === "number" ? remarks.toLocaleString("en-US") : remarks;
  }

  return newRemark;
};

const getFormattedFileDate = (startDate, endDate) => {
  const fomattedStartDate = moment(startDate).format("MMMM DD, YYYY");
  const startDateMonth = moment(startDate).format("MMMM");
  const fomattedEndDate = moment(endDate).format("MMMM DD, YYYY");
  const endDateMonth = moment(endDate).format("MMMM");

  let date = `Date: ${moment(startDate).format("MMMM DD, YYYY")}`;
  let indent = 152;

  if (endDate && fomattedStartDate !== fomattedEndDate) {
    if (startDateMonth === endDateMonth) {
      date = `Date: ${moment(startDate).format("MMMM DD")}-${moment(
        endDate
      ).format("DD, YYYY")}`;
      indent = 147;
    } else {
      date = `Date: ${moment(startDate).format("MMMM DD")}-${moment(
        endDate
      ).format("MMMM DD, YYYY")}`;
      indent = 126;
    }
  }

  return { date, indent };
};

const getFileName = (startDate, endDate) => {
  const fomattedStartDate = moment(startDate).format("MMMM DD, YYYY");
  const startDateMonth = moment(startDate).format("MMMM");
  const fomattedEndDate = moment(endDate).format("MMMM DD, YYYY");
  const endDateMonth = moment(endDate).format("MMMM");

  let date = moment(startDate).format("MM_DD");

  if (endDate && fomattedStartDate !== fomattedEndDate) {
    if (startDateMonth === endDateMonth) {
      date = `${moment(startDate).format("MM_DD")}-${moment(endDate).format(
        "DD"
      )}`;
    } else {
      date = `${moment(startDate).format("MM_DD")}-${moment(endDate).format(
        "MM_DD"
      )}`;
    }
  }

  return date;
};

export const generateExcel = (data) => {
  try {
    const formattedData = [];

    if (data?.length) {
      data.forEach((agency) => {
        const agen = agency.id;

        if (agency?.projects?.length) {
          agency?.projects?.forEach((project) => {
            if (project?.recipients?.length) {
              project?.recipients?.forEach((recipient) => {
                const supporting = recipient?.documents?.supporting || [];

                const files = supporting?.join("\n");

                formattedData.push({
                  ID: recipient.id,
                  Congressman: recipient?.congressman?.fullName,
                  "Congressman Contact": recipient?.congressman?.contact,
                  Agency: agency?.name,
                  Project: project?.name,
                  Budget: recipient?.budget,
                  "Date Requested": getFormattedDateString(
                    recipient?.createdAt,
                    "MMM-dd-yyy"
                  ),
                  "Date Approved": getFormattedDateString(
                    recipient?.dateApproved,
                    "MMM-dd-yyy"
                  ),
                  Status:
                    modifiedStatuses[recipient?.status] ?? recipient?.status,
                  "Allocation Type": recipient?.allocationType,
                  "ATC Branch": recipient?.allocationName || "",
                  COS: `${recipient?.congressman?.cosFirstName || ""} ${
                    recipient?.congressman?.cosLastName || ""
                  }`,
                  "COS Contact": recipient?.congressman?.cosContact,
                  "Allocation Contact Person": recipient?.allocationContactName,
                  "Allocation Contact Number":
                    recipient?.allocationContactNumber,
                  "Implementing Agency": recipient?.implementing?.acronym,
                  "Implementing Office": recipient?.implementingOffice,
                  Province: recipient?.province?.name,
                  Municipality: recipient?.municipality?.name,
                  Facility: agen === 8 ? recipient?.institution?.name : null,
                  "Fund Source": recipient?.fundSource,
                  Justification: recipient?.justification,
                  "LGSF Tracking Code": recipient?.otherInfo,
                  "Project Title":
                    agen === 3 || agen === 14
                      ? recipient?.institution?.name
                      : null,
                  "Name of TVI":
                    // eslint-disable-next-line no-nested-ternary
                    agen === 27
                      ? recipient?.tviName === null
                        ? recipient?.institution?.name
                        : recipient?.tviName
                      : null,
                  "Number of Slots": recipient?.slots,
                  "Supporting Documents": files,
                  "Editor Notes": recipient?.editorNotes,
                });
              });
            }
          });
        }
      });
    }

    // SORT BY dateApproved DESCENDING
    formattedData.sort((a, b) => {
      const dateA = a["Date Approved"] ? new Date(a["Date Approved"]) : null;
      const dateB = b["Date Approved"] ? new Date(b["Date Approved"]) : null;

      if (dateA === null && dateB === null) return 0;
      if (dateA === null) return 1;
      if (dateB === null) return -1;

      return dateB - dateA;
    });

    if (formattedData.length !== 0) {
      const worksheet = XLSX.utils.json_to_sheet(formattedData);

      worksheet["!cols"] = [
        { wpx: 50 },
        { wpx: 275 },
        { wpx: 157 },
        { wpx: 348 },
        { wpx: 72 },
        { wpx: 134 },
        { wpx: 145 },
        { wpx: 145 },
        { wpx: 100 },
        { wpx: 168 },
        { wpx: 275 },
        { wpx: 157 },
        { wpx: 219 },
        { wpx: 275 },
        { wpx: 157 },
        { wpx: 146 },
        { wpx: 160 },
        { wpx: 200 },
        { wpx: 190 },
        { wpx: 190 },
        { wpx: 190 },
        { wpx: 400 },
        { wpx: 190 },
        { wpx: 275 },
        { wpx: 275 },
        { wpx: 190 },
        { wpx: 400 },
        { wpx: 190 },
      ];

      for (let i = 2; i < formattedData.length + 2; i += 1) {
        worksheet[`F${i}`].z = "#,##0.00";
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report 1");

      XLSX.writeFile(
        workbook,
        `Report (${format(new Date(), "uu-LL-dd HHmm")}).xlsx`
      );
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    Toast({ type: 4, content: "Failed to generate EXCEL file." });
  }
};

const getHeaderColor = (id) => {
  let headerColor = "#000000";

  if (REPORT_COLOR_SCHEMES[id]) {
    headerColor = REPORT_COLOR_SCHEMES[id].header;
  }

  return headerColor;
};

const getColumnHeaderColor = (id) => {
  let columnHeaderColor = "#FC0B1B";

  if (REPORT_COLOR_SCHEMES[id]) {
    columnHeaderColor = REPORT_COLOR_SCHEMES[id].rowColumns;
  }

  return columnHeaderColor;
};

const generatePDF = (data, type, startDate, endDate) => {
  try {
    const doc = new JSPDF();

    const reportType = type === "request" ? "Request" : "Endorsement";

    let yPosition = 30;

    data.forEach((table) => {
      // Check if there's enough space on the current page
      const pageHeight = doc.internal.pageSize.height;

      if (yPosition > pageHeight - 100) {
        doc.addPage();
        yPosition = 30; // Reset yPosition for new page
      }

      // Add agency name as table header
      const tableWidth = 180;
      const rectHeight = 10;
      const rectX = 14;
      const rectY = yPosition;

      // Draw the rectangle
      doc.setFillColor(getHeaderColor(table.agencyId));
      doc.rect(rectX, rectY, tableWidth, rectHeight, "F");

      // Position the text in the center of the rectangle
      const textX = rectX + tableWidth / 2;
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text(table.agency, textX, yPosition + 7, { align: "center" });

      yPosition += 10;

      // Prepare table data
      const tableData = table.recipients.map((recipient, index) => [
        getTableIndex(index, table.recipients),
        recipient.cong || "None",
        recipient.amount.toLocaleString("en-US"),
        formatRemarks(recipient.remarks, type),
      ]);

      const thirdColumnName = type === "request" ? "Recommendation" : "Remarks";

      // Add table
      doc.autoTable({
        startY: yPosition,
        head: [["", "House Member", "Amount", thirdColumnName]],
        body: tableData,
        theme: "grid",
        styles: { fontSize: 10 },
        headStyles: { fillColor: getColumnHeaderColor(table.agencyId) },
        columnStyles: {
          0: { halign: "center", cellWidth: 10 },
          1: { cellWidth: 70 },
          2: { halign: "right", cellWidth: 40 },
          3: { halign: "right", cellWidth: 60 },
        },
        didDrawPage: () => {
          // Add header to each new page
          doc.setFontSize(16);
          doc.setTextColor(0);
          doc.setFont("helvetica", "italic", "bold");
          doc.text(`${reportType} Report`, 14, 20);
          doc.setFontSize(11);
          doc.setFont("helvetica", "italic", "bold");

          const { date, indent } = getFormattedFileDate(startDate, endDate);

          doc.text(date, indent, 20);
        },
        didParseCell: (tables) => {
          if (
            tables.section === "head" &&
            (tables.column.index === 2 || tables.column.index === 3)
          ) {
            // eslint-disable-next-line no-param-reassign
            tables.cell.styles.halign = "right";
          }
        },

        margin: { top: 30, bottom: 40 },
      });

      yPosition = doc.lastAutoTable.finalY + 7;

      // Add footer note for each table
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.setFont("helvetica", "italic", "normal");

      doc.text(
        "The above mentioned endorsements are subject to the agency's guidelines, review and approval.",
        14,
        yPosition
      );

      yPosition += 15;
    });

    // Add "Nothing Follows" at the end
    doc.setFontSize(13);
    doc.setFont("helvetica", "italic", "bold");
    doc.text("— NOTHING FOLLOWS —", 105, yPosition, { align: "center" });

    // Add page numbers
    const pageCount = doc.internal.getNumberOfPages();

    // eslint-disable-next-line no-plusplus
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(11);
      doc.text(`Page ${i} of ${pageCount}`, 14, 290);
      const { date, indent } = getFormattedFileDate(startDate, endDate);

      doc.text(date, indent, 290);
    }

    // Save the PDF
    const fileDate = getFileName(startDate, endDate);
    doc.save(`${reportType} Report ${fileDate}.pdf`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
    Toast({ type: 4, content: "Failed to generate PDF file." });
  }
};

const generateDoc = async (data, type, startDate, endDate) => {
  try {
    const reportType = type === "request" ? "Request" : "Endorsement";

    const children = [];

    children.push(
      new Paragraph({
        spacing: { after: 500 },
      })
    );

    data.forEach((table) => {
      const rows = [];

      rows.push(
        new TableRow({
          height: { value: 400, rule: HeightRule.ATLEAST },
          children: [
            new TableCell({
              columnSpan: 4,
              verticalAlign: VerticalAlign.CENTER,
              shading: {
                type: "clear",
                color: "auto",
                fill: getHeaderColor(table.agencyId).split("#")[1],
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: table.agency,
                      bold: true,
                      font: "Arial",
                      color: "FFFFFF",
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                }),
              ],
            }),
          ],
        })
      );

      // Header row
      rows.push(
        new TableRow({
          height: { value: 400, rule: HeightRule.ATLEAST },
          children: [
            new TableCell({
              verticalAlign: VerticalAlign.CENTER,
              shading: {
                type: "clear",
                color: "auto",
                fill: getColumnHeaderColor(table.agencyId).split("#")[1],
              },
              children: [
                new Paragraph({
                  text: "",
                  alignment: AlignmentType.CENTER,
                }),
              ],
            }),
            new TableCell({
              verticalAlign: VerticalAlign.CENTER,
              shading: {
                type: "clear",
                color: "auto",
                fill: getColumnHeaderColor(table.agencyId).split("#")[1],
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "House Member",
                      bold: true,
                      font: "Arial",
                      color: "FFFFFF",
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              verticalAlign: VerticalAlign.CENTER,
              shading: {
                type: "clear",
                color: "auto",
                fill: getColumnHeaderColor(table.agencyId).split("#")[1],
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Amount",
                      bold: true,
                      font: "Arial",
                      color: "FFFFFF",
                    }),
                  ],
                  alignment: AlignmentType.RIGHT,
                }),
              ],
            }),
            new TableCell({
              verticalAlign: VerticalAlign.CENTER,
              shading: {
                type: "clear",
                color: "auto",
                fill: getColumnHeaderColor(table.agencyId).split("#")[1],
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: type === "request" ? "Recommendation" : "Remarks",
                      bold: true,
                      font: "Arial",
                      color: "FFFFFF",
                    }),
                  ],
                  alignment: AlignmentType.RIGHT,
                }),
              ],
            }),
          ],
        })
      );

      // Recipient rows
      table.recipients.forEach((recipient, index) => {
        rows.push(
          new TableRow({
            height: { value: 400, rule: HeightRule.ATLEAST },
            children: [
              new TableCell({
                verticalAlign: VerticalAlign.CENTER,
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: String(getTableIndex(index, table.recipients)),
                        font: "Arial",
                      }),
                    ],
                    alignment: AlignmentType.CENTER,
                  }),
                ],
              }),
              new TableCell({
                verticalAlign: VerticalAlign.CENTER,
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: recipient.cong || "None",
                        font: "Arial",
                      }),
                    ],
                  }),
                ],
              }),
              new TableCell({
                verticalAlign: VerticalAlign.CENTER,
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: (Number(recipient.amount) || 0).toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        ),
                        font: "Arial",
                      }),
                    ],
                    alignment: AlignmentType.RIGHT,
                  }),
                ],
              }),
              new TableCell({
                verticalAlign: VerticalAlign.CENTER,
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: String(
                          formatRemarks(recipient.remarks, type) || ""
                        ),
                        font: "Arial",
                      }),
                    ],
                    alignment: AlignmentType.RIGHT,
                  }),
                ],
              }),
            ],
          })
        );
      });

      children.push(
        new Table({
          rows,
          width: { size: 100, type: WidthType.PERCENTAGE },
          layout: TableLayoutType.FIXED,
          borders: {
            top: { style: "single", size: 1 },
            bottom: { style: "single", size: 1 },
            left: { style: "single", size: 1 },
            right: { style: "single", size: 1 },
            insideHorizontal: { style: "single", size: 1 },
            insideVertical: { style: "single", size: 1 },
          },
          columnWidths: [500, 4300, 2000, 2000],
        })
      );

      children.push(
        new Paragraph({
          spacing: { before: 200, after: 500 },
          children: [
            new TextRun({
              text: "The above mentioned endorsements are subject to the agency's guidelines, review and approval.",
              italics: true,
              size: 24,
              font: "Helvetica",
            }),
          ],
        })
      );
    });

    children.push(
      new Paragraph({
        spacing: { before: 700 },
        children: [
          new TextRun({
            text: "— NOTHING FOLLOWS —",
            bold: true,
            italics: true,
            size: 26,
            font: "Helvetica",
          }),
        ],
        alignment: AlignmentType.CENTER,
      })
    );

    const docxDoc = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 1000,
                right: 1500,
                bottom: 1000,
                left: 1500,
              },
            },
          },
          headers: {
            default: new Header({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${reportType} Report`,
                      bold: true,
                      italics: true,
                      font: "Helvetica",
                      size: 32,
                    }),
                  ],
                }),
              ],
            }),
          },
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Page ",
                      italics: true,
                      size: 24,
                      font: "Helvetica",
                    }),
                    new TextRun({
                      children: [PageNumber.CURRENT],
                      italics: true,
                      size: 24,
                      font: "Helvetica",
                    }),
                    new TextRun({
                      text: " of ",
                      italics: true,
                      size: 24,
                      font: "Helvetica",
                    }),
                    new TextRun({
                      children: [PageNumber.TOTAL_PAGES],
                      italics: true,
                      size: 24,
                      font: "Helvetica",
                    }),
                  ],
                  alignment: AlignmentType.LEFT,
                }),
              ],
            }),
          },
          children,
        },
      ],
    });

    const fileDate = getFileName(startDate, endDate);
    const blob = await Packer.toBlob(docxDoc);
    saveAs(blob, `${reportType} Report ${fileDate}.docx`);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
    Toast({ type: 4, content: "Failed to generate DOCX file." });
  }
};

export const generateReport = (data, type, exportType, startDate, endDate) => {
  const formattedData = [];

  data.forEach((agency) => {
    const agencyName = `${agency?.acronym}`;

    if (agency?.projects?.length) {
      agency?.projects?.forEach((project) => {
        const projectName = `${agencyName} ${project?.name}`;
        const projectId = project?.id;
        const recommendedBudget = project?.recommendedBudget;

        const newData = {
          agency: projectName,
          agencyId: agency.id,
          recipients: getProjectRecipients(
            projectId,
            project?.recipients,
            type,
            recommendedBudget
          ),
        };

        formattedData.push(newData);
      });
    }
  });

  if (exportType === "pdf") {
    return generatePDF(formattedData, type, startDate, endDate);
  }

  return generateDoc(formattedData, type, startDate, endDate);
};
